import { FunctionComponent, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  ADDON_MODAL_CONSTANTS,
  BUNDLE_ADDON_MODAL_STEP,
  LEARN_MORE_ADDON_MODAL_STEP,
  PURCHASE_ADDON_MODAL_STEP,
  PURCHASE_SUCCESS_ADDON_MODAL_STEP,
  UPSELL_FUNNEL_ADDON_MODAL_STEP
} from "rivals/components/shared/AddonOptions/constants";
import CustomButton from "rivals/components/shared/CustomButton";
import Modal from "rivals/components/shared/Modal";
import { getRailsConstants } from "rivals/redux/rails/actions";
import { RailsConstantsKeys } from "rivals/redux/rails/reducer";
import {
  clearBundle,
  resetAddOnPricing as resetAddOnPricingAction,
  setModalStep,
  setUpsellStepOption
} from "rivals/redux/subscriptionAddons/actions";
import {
  getAddOnPurchaseSuccess,
  getModalStep,
  getSubmitting
} from "rivals/redux/subscriptionAddons/selectors";
import { getCurrentUser as getCurrentUserAction } from "rivals/redux/user/actions";
import { isAddOnSubscribed } from "rivals/shared/utils/addons";
import { setData } from "rivals/shared/utils/qa";
import styles from "./AddonModal.module.scss";
import BundleAddOnsStep from "./Steps/BundleAddOnsStep";
import LearnMoreStep from "./Steps/LearnMoreStep";
import PurchaseStep from "./Steps/PurchaseStep";
import PurchaseSuccessStep from "./Steps/PurchaseSuccessStep";
import UpsellFunnelStep from "./Steps/UpsellFunnelStep";
import { OwnProps } from "./types";

const { ALL_ACCESS, AD_LITE } = ADDON_MODAL_CONSTANTS;

const AddonModal: FunctionComponent<OwnProps> = ({
  closeModal,
  existingSpyAddOns = [],
  showModal = true,
  addOn,
  addOns
}) => {
  const dispatch = useDispatch();
  const modalStep = useSelector(getModalStep);
  const purchaseSuccessSelector = useSelector(getAddOnPurchaseSuccess);
  const submitting = useSelector(getSubmitting);
  const alreadySubscribed = isAddOnSubscribed(addOn) || false;
  const dataPhilter = "addon-modal";

  useEffect(() => {
    if (purchaseSuccessSelector) {
      dispatch(getCurrentUserAction());
      dispatch(setModalStep(PURCHASE_SUCCESS_ADDON_MODAL_STEP));
    }
  }, [purchaseSuccessSelector]);

  useEffect(() => {
    dispatch(getRailsConstants([RailsConstantsKeys.SUBSCRIPTION_PRICING]));
  }, []);

  const closeAndReset = (submitted: boolean, callback?: () => void): void => {
    if (callback) {
      callback();
    }
    closeModal(submitted);
    dispatch(resetAddOnPricingAction());
    dispatch(clearBundle());
    dispatch(setModalStep());
    dispatch(setUpsellStepOption());
  };

  const addClick = (): void => {
    if (addOn.type === ALL_ACCESS || addOn.type === AD_LITE) {
      dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
    } else {
      dispatch(setModalStep(UPSELL_FUNNEL_ADDON_MODAL_STEP));
    }
  };

  const onGoBack = (step: string): void => {
    dispatch(setModalStep(step));
  };

  const onMoveFromBundleAddOnsStep = (): void => {
    dispatch(setModalStep(PURCHASE_ADDON_MODAL_STEP));
  };

  const learnMoreStep: JSX.Element = LearnMoreStep({
    addClick,
    addOn,
    alreadySubscribed,
    parentPhilter: dataPhilter
  });

  const upsellFunnelStep: JSX.Element = UpsellFunnelStep({
    addOn,
    addOns,
    closeButtonClick: closeAndReset,
    parentPhilter: dataPhilter
  });

  const bundleStep: JSX.Element | null = BundleAddOnsStep({
    addOns,
    closeAndReset,
    onGoBack,
    onMoveToNextStep: onMoveFromBundleAddOnsStep,
    parentPhilter: dataPhilter
  });

  const purchaseStep: JSX.Element = PurchaseStep({
    addOn,
    closeAndReset,
    existingSpyAddOns,
    onGoBack,
    parentPhilter: dataPhilter,
    submitting
  });

  const purchaseSuccessStep: JSX.Element = PurchaseSuccessStep({
    addOn,
    closeButtonClick: closeAndReset,
    parentPhilter: dataPhilter
  });

  const StepsWrapper = (): JSX.Element | null => {
    switch (modalStep) {
      case LEARN_MORE_ADDON_MODAL_STEP:
        return learnMoreStep;
      case UPSELL_FUNNEL_ADDON_MODAL_STEP:
        return upsellFunnelStep;
      case BUNDLE_ADDON_MODAL_STEP:
        return bundleStep;
      case PURCHASE_ADDON_MODAL_STEP:
        return purchaseStep;
      case PURCHASE_SUCCESS_ADDON_MODAL_STEP:
        return purchaseSuccessStep;
      default:
        return null;
    }
  };

  return (
    <>
      {showModal && (
        <Modal
          handleCancel={(): void => {
            closeAndReset(false);
          }}
          handleConfirm={(): void => {
            closeAndReset(false);
          }}
          open={showModal}
          showFooter={false}
          width={"fit-content"}
          wrapClassName={styles.modalWrapper}
        >
          <div className={styles.modalStyles} {...setData(dataPhilter, "")}>
            <div className={styles.topBar}>
              <CustomButton
                ariaLabel="hide"
                className={styles.closeIcon}
                onClick={(): void => closeAndReset(false)}
                philter={`${dataPhilter}-close`}
              />
            </div>
            <StepsWrapper />
          </div>
        </Modal>
      )}
    </>
  );
};

export default AddonModal;
